<script setup lang="ts">
  import { computed } from "vue";
  import EmojiPicker from "vue3-emoji-picker";
  import "vue3-emoji-picker/css";
  import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
  import { Button } from "@/components/ui/button";
  import { Smile } from "lucide-vue-next";
  import { epistoFavorites } from "@utils/constants/emojiGroups";

  interface EmojiData {
    i: string;
    n: string[];
    r: string;
    t: string;
    u: string;
  }

  const props = withDefaults(
    defineProps<{
      open?: boolean;
    }>(),
    {
      open: false,
    },
  );

  const emit = defineEmits<{
    select: [emoji: string];
    "update:open": [value: boolean];
  }>();

  const isOpen = computed({
    get: () => props.open,
    set: (value) => emit("update:open", value),
  });

  const additionalGroups = { episto_favorites: epistoFavorites };

  const handleSelect = (emoji: EmojiData): void => { emit("select", emoji.i) };
</script>

<template>
  <Popover v-model:open="isOpen">
    <PopoverTrigger as-child>
      <!-- mousedown.prevent keeps the focus in the editor: RichInput only renders this picker while the editor
           is focused, so a focus-stealing mousedown would unmount the button before its click fires. -->
      <Button
        variant="ghost"
        size="icon"
        class="h-6 w-6"
        @mousedown.prevent>
        <Smile class="w-4 h-4 text-muted-foreground" />
      </Button>
    </PopoverTrigger>
    <PopoverContent
      class="w-auto p-0"
      align="end"
      side="top"
      @focus-outside.prevent>
      <EmojiPicker
        :native="true"
        :disable-skin-tones="true"
        display-recent
        :additional-groups="additionalGroups"
        @select="handleSelect" />
    </PopoverContent>
  </Popover>
</template>
